import type { TranslationKey } from '../../i18n';

import type { AdminRole } from '../../types/api';

export interface NavItem {
  to: string;
  /** Translation key, resolved at render — labels cannot be baked in. */
  label: TranslationKey;
  icon: string;
  /**
   * The permission that puts this screen on the menu.
   *
   * A module name, checked against the grid the API sends with the profile —
   * so a superadmin changing what a role may do changes what that role's
   * console offers, without a deploy. Items with neither this nor `minRole`
   * are open to anyone signed in.
   */
  module?: string;
  /**
   * Seniority, for the two screens that are structurally superadmin-only and
   * deliberately not in the permission grid: managing staff, and editing the
   * grid itself. A permission that could grant those would be a permission one
   * role could use to promote itself.
   */
  minRole?: AdminRole;
  end?: boolean;
}

export interface NavGroup {
  label: TranslationKey;
  items: NavItem[];
}

/**
 * Navigation is grouped by what the operator is doing, not alphabetically.
 * Audit stands alone at the bottom because it is the record of everything above
 * it rather than another task.
 */
export const NAV_GROUPS: NavGroup[] = [
  {
    label: 'nav.group.watch',
    items: [
      { to: '/', label: 'nav.dashboard', icon: '◉', end: true },
      { to: '/draws', label: 'nav.draws', icon: '⬡', module: 'draws' },
      { to: '/games', label: 'nav.games', icon: '⚇', module: 'games' },
    ],
  },
  {
    label: 'nav.group.people',
    items: [
      { to: '/review', label: 'nav.review', icon: '⚑', module: 'review' },
      { to: '/players', label: 'nav.players', icon: '◎', module: 'players' },
      // The test-player screen creates and deletes accounts, so it follows the
      // permission that actually gates what it does rather than a rank.
      { to: '/test-players', label: 'nav.testPlayers', icon: '⚗', module: 'players' },
      { to: '/admins', label: 'nav.admins', icon: '★', minRole: 'superadmin' },
      { to: '/permissions', label: 'nav.permissions', icon: '⚿', minRole: 'superadmin' },
      { to: '/sessions', label: 'nav.sessions', icon: '◷', module: 'sessions' },
    ],
  },
  {
    label: 'nav.group.money',
    items: [
      { to: '/bets', label: 'nav.bets', icon: '▤', module: 'bets' },
      { to: '/ledger', label: 'nav.ledger', icon: '⇄', module: 'ledger' },
      { to: '/reports', label: 'nav.reports', icon: '◈', module: 'reports' },
    ],
  },
  {
    label: 'nav.group.control',
    items: [
      { to: '/referrals', label: 'nav.referrals', icon: '⌘', module: 'referrals' },
      { to: '/notifications', label: 'nav.notifications', icon: '✉', module: 'settings' },
      { to: '/settings', label: 'nav.settings', icon: '⚙', module: 'settings' },
    ],
  },
];

export const AUDIT_ITEM: NavItem = {
  to: '/audit',
  label: 'nav.audit',
  icon: '☰',
  module: 'audit',
};
